// The four rules every read obeys, as a state machine with no React in it.
//
// WHY IT IS SEPARATE FROM THE HOOK. `read.ts` is the layer that decides what
// a screen shows — loading, data, stale data, or a failure that keeps the
// data — and it had no test of any kind: `api.test.ts` covers the fetch below
// it and `storage.test.ts` the layer beside it (AUDIT-2026-09-19 F23). It
// could not have one while the rules lived inside a `useEffect`, because
// `apps/web` has no DOM to render a hook into and the four transitions are
// not things a walk can produce on demand — a failing refresh over data
// already held needs a server that answers twice and fails the second time.
//
// So the rules are here, as `reduce` and `shown`, and `read.ts` is the
// adapter that turns effects into events. Nothing in this file imports React,
// touches the network, or reads a clock; every input arrives as an argument.
//
// THE RULES, and each one is a bug this codebase has already had:
//
//   AN ANSWER FOR SOMEBODY ELSE IS NOT AN ANSWER. `shown` compares the
//   answer's identity against the one being asked for, every render. A flag
//   set in an effect has the same hole, because the gap is a render.
//
//   A RELOAD OF DATA WE HOLD DOES NOT BLANK THE SCREEN. `ask` over a held
//   value emits `ready` with `stale: true` rather than `loading`.
//
//   A FAILED REFRESH KEEPS ITS VALUE. `fail` carries the held value forward
//   as `lastValue`, so a screen can choose between showing it with a warning
//   and showing nothing (ICD-U01 §4).
//
//   A RETRY OF A FAILED READ CLEARS THE FAILURE. `ask` with nothing held
//   drops an answer for the SAME identity, so "Try again" does not leave the
//   old error on screen looking like nothing happened; an answer for a
//   different identity is left alone, because the render already ignores it
//   and dropping it would blank a screen that is about to be replaced
//   (review 2026-09-18, L5).
//
//   WHAT THE DEVICE KEPT IS THE LAST VALUE WHEN NOTHING NEWER IS (B-557). A
//   failure can carry the copy this device kept of the last answer
//   (`kept.ts`); it is `lastValue` only where the reader holds nothing of its
//   own, which is always newer. And a retry over it keeps it on screen, still
//   said to be old, rather than "Loading…" for as long as a dead connection
//   takes to fail — but only through another failure that is no answer: a
//   refusal, or a shape this build cannot read, is said in its own words
//   rather than hidden under the copy.

import type { Failure, Snapshot } from '@fsp/domain';

/** What a reader holds between renders. */
export interface ReaderState<T> {
  /**
   * The newest good value and whose question it answered.
   *
   * ONE SLOT, and it survives an identity change on purpose: a read that is
   * replaced before it answers must not lose what the screen was already
   * showing. It is not a cache of every subject — once another identity has
   * answered, this holds that one, and going back is a fresh read. Two jobs:
   * `ask` for the identity it holds emits a stale `ready` instead of a
   * spinner, and `fail` for that identity keeps the value as `lastValue`.
   */
  held: { identity: string; value: T; asOf: string } | null;
  /** The last answer, and the question it was an answer to. */
  answer: { identity: string; snapshot: Snapshot<T> } | null;
}

export type ReaderEvent<T> =
  /** A read is starting for this identity. */
  | { kind: 'ask'; identity: string }
  /** It answered. `value` is null for the reads where "no such thing" is an answer. */
  | { kind: 'ok'; identity: string; value: T; asOf: string }
  /** It did not. An abort is not a failure and never reaches here. */
  | {
      kind: 'fail';
      identity: string;
      failure: Failure;
      /** What this device kept of the last answer, read against this reader's schema. */
      kept?: { value: T; asOf: string };
    };

export const EMPTY: ReaderState<never> = { held: null, answer: null };

/** The failures that are no answer about the thing asked: a copy on screen stays through them. */
const NO_ANSWER: ReadonlySet<string> = new Set([
  'unreachable',
  'timeout',
  'unavailable',
  'rate_limited',
]);

export const LOADING: Snapshot<never> = { state: 'loading' };

/**
 * One string that says what is being asked for. Null means nothing is.
 *
 * Length-prefixed rather than joined by a separator, so no pair of key and
 * path can ever spell the same string as another pair. A control character
 * would do as well and would be worse: Prettier rewrites the escape into a
 * literal, and a raw one in a source file is invisible to every reader.
 */
export function identityOf(path: string | null, key = ''): string | null {
  return path === null ? null : `${String(key.length)}:${key}${path}`;
}

/** What the reader holds after one thing happens to it. */
export function reduce<T>(state: ReaderState<T>, event: ReaderEvent<T>): ReaderState<T> {
  const have = state.held?.identity === event.identity ? state.held : null;
  switch (event.kind) {
    case 'ask':
      if (have !== null) {
        return {
          ...state,
          answer: {
            identity: event.identity,
            snapshot: { state: 'ready', value: have.value, asOf: have.asOf, stale: true },
          },
        };
      }
      if (
        state.answer?.identity === event.identity &&
        state.answer.snapshot.state === 'failed' &&
        state.answer.snapshot.lastValue !== null
      )
        return state;
      return state.answer?.identity === event.identity ? { ...state, answer: null } : state;

    case 'ok':
      return {
        held: { identity: event.identity, value: event.value, asOf: event.asOf },
        answer: {
          identity: event.identity,
          snapshot: { state: 'ready', value: event.value, asOf: event.asOf, stale: false },
        },
      };

    case 'fail': {
      // What is on screen for this identity already, from the device, kept
      // only through a failure that is no answer.
      const shown =
        NO_ANSWER.has(event.failure.code) &&
        state.answer?.identity === event.identity &&
        state.answer.snapshot.state === 'failed'
          ? state.answer.snapshot
          : null;
      const last =
        have ??
        event.kept ??
        (shown !== null && shown.lastValue !== null
          ? { value: shown.lastValue, asOf: shown.lastAsOf }
          : null);
      return {
        ...state,
        answer: {
          identity: event.identity,
          snapshot: {
            state: 'failed',
            failure: event.failure,
            lastValue: last?.value ?? null,
            lastAsOf: last?.asOf ?? null,
          },
        },
      };
    }
  }
}

/**
 * What the screen sees, computed in render rather than stored.
 *
 * `idle` is the answer to "nothing to ask for", which is an ANSWER and the
 * empty one (ICD-U01 §9). It is passed in because it carries an instant and
 * this file reads no clock.
 */
export function shown<T>(
  state: ReaderState<T>,
  asked: { identity: string | null; pending: boolean; idle: Snapshot<T> },
): Snapshot<T> {
  if (asked.pending) return LOADING;
  if (asked.identity === null) return asked.idle;
  return state.answer?.identity === asked.identity ? state.answer.snapshot : LOADING;
}
